import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Heart, Clock, Loader2, LogOut, Home } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useEnrichedMatches } from '../../hooks/useEnrichedMatches';
import { useUser } from '../../hooks/useUsers';
import { useTenantInquiries } from '../../hooks/useTenantInquiries';
import { calculateCompatibilityScore } from '../../utils/compatibility';
import BottomNav from '../../components/BottomNav';
import TenantNav from '../../components/TenantNav';
import { InquiryStatusBadge } from '../../components/ui';
import { formatDistanceToNow } from 'date-fns';

const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { user: currentUserData } = useUser(currentUser?.uid);
  const { enrichedMatches, loading } = useEnrichedMatches();
  const { inquiries } = useTenantInquiries(currentUser?.uid);

  // Sort matches by lastMessageAt (most recent first)
  const sortedMatches = useMemo(() => {
    return [...enrichedMatches].sort((a, b) => {
      const aTime = a?.lastMessageAt?.toMillis?.() || 0;
      const bTime = b?.lastMessageAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
  }, [enrichedMatches]);

  const getUnreadCount = (matchId: string) => {
    const match = enrichedMatches.find(m => m.matchId === matchId);
    if (!match?.unreadCount || !currentUser?.uid) return 0;
    return match.unreadCount[currentUser.uid] || 0;
  };

  const formatLastMessageTime = (timestamp: any) => {
    if (!timestamp) return 'No messages yet';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary-300 animate-spin mx-auto mb-4" />
              <p className="text-text-secondary font-semibold">Loading your chats...</p>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!sortedMatches.length && !inquiries.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center max-w-md">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="w-12 h-12 text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-3">No Chats Yet</h2>
              <p className="text-text-secondary mb-6">
                Start swiping to find your perfect roommate match and begin chatting!
              </p>
              <button
                onClick={() => navigate('/tenant/swipe')}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Heart className="w-5 h-5" />
                Start Swiping
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 py-6 pb-24 md:pb-10">
      <div className="max-w-6xl mx-auto space-y-4">
        <TenantNav />
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Messages</h1>
          <p className="text-text-secondary">
            {sortedMatches.length} {sortedMatches.length === 1 ? 'conversation' : 'conversations'}
          </p>
        </div>

        {/* Property inquiries (3-way chat with landlords) */}
        {inquiries.length > 0 && (
          <div className="space-y-3 mb-4">
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
              Property inquiries
            </h2>
            {inquiries.map((inquiry) => {
              const unread = inquiry.chatUnread && currentUser?.uid
                ? inquiry.chatUnread[currentUser.uid] || 0
                : 0;
              return (
                <button
                  key={inquiry.id}
                  onClick={() => navigate(`/tenant/inquiry/${inquiry.id}`)}
                  className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 text-left flex items-center gap-4"
                >
                  <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center text-white flex-shrink-0">
                    <Home className="w-6 h-6" />
                    {unread > 0 && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                        {unread > 9 ? '9+' : unread}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-text-primary truncate">
                        {inquiry.propertyTitle || 'Property inquiry'}
                      </h3>
                      <InquiryStatusBadge status={inquiry.status} />
                    </div>
                    <p className="text-sm text-text-secondary truncate">Chat with the landlord</p>
                  </div>
                  <MessageCircle className="w-5 h-5 text-text-tertiary flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}

        {/* Chat List */}
        <div className="space-y-3">
          {sortedMatches.map((match) => {
            const unreadCount = getUnreadCount(match.matchId);
            // Compute live so it matches the Matches page (the stored
            // match.compatibilityScore is often absent/stale).
            const compatibilityScore = currentUserData
              ? calculateCompatibilityScore(currentUserData, match.user)
              : match.compatibilityScore ?? 0;

            return (
              <button
                key={match.matchId}
                onClick={() => navigate(`/tenant/chat/${match.matchId}`)}
                className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 text-left group"
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-white font-bold text-xl">
                      {(match.user?.name?.[0] ?? '?').toUpperCase()}
                    </div>
                    {unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </div>
                    )}
                  </div>

                  {/* Chat Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1">
                      <h3 className="font-semibold text-text-primary truncate pr-2">
                        {match.user?.name || 'Unknown User'}
                      </h3>
                      <span className="text-xs text-text-tertiary flex-shrink-0 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatLastMessageTime(match.lastMessageAt)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-text-secondary truncate">
                        {match.user?.city || 'Unknown location'} • {compatibilityScore}% match
                      </span>
                    </div>
                  </div>

                  {/* Arrow Icon */}
                  <div className="flex-shrink-0 text-text-tertiary group-hover:text-primary-400 transition-colors">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                </div>

                {/* Shared Interests Preview */}
                {match.sharedLifestyle && match.sharedLifestyle.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {match.sharedLifestyle.slice(0, 3).map((interest, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded-full"
                        >
                          {interest}
                        </span>
                      ))}
                      {match.sharedLifestyle.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-text-tertiary rounded-full">
                          +{match.sharedLifestyle.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ChatList;
